import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Snackbar, Alert, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  TextField
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Layout from '../components/Layout';
import FlowCardGrid from '../components/voice-flow/FlowCardGrid';
import ScriptEditor from '../components/voice-flow/ScriptEditor';
import voiceFlowService from '../services/voiceFlowService';

const VoiceFlowPage = () => {
  // 列表狀態
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggerFilter, setTriggerFilter] = useState('all');

  // 編輯器狀態
  const [editingFlow, setEditingFlow] = useState(null);

  // 對話框
  const [deleteDialog, setDeleteDialog] = useState({ open: false, flow: null });
  const [duplicateDialog, setDuplicateDialog] = useState({ open: false, flow: null, newName: '' });

  // 通知
  const [notification, setNotification] = useState({ open: false, message: '', type: 'success' });

  // 載入流程列表
  const fetchFlows = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (triggerFilter !== 'all') params.triggerType = triggerFilter;
    const result = await voiceFlowService.listFlows(params);
    if (result.success) {
      setFlows(result.data || []);
    } else {
      setNotification({ open: true, message: result.message, type: 'error' });
    }
    setLoading(false);
  }, [triggerFilter]);

  useEffect(() => {
    fetchFlows();
  }, [fetchFlows]);

  // 切換啟用狀態
  const handleToggle = async (flowId) => {
    const result = await voiceFlowService.toggleFlow(flowId);
    if (result.success) {
      setFlows(prev => prev.map(f =>
        f.id === flowId ? { ...f, isActive: result.data.isActive } : f
      ));
    } else {
      setNotification({ open: true, message: result.message, type: 'error' });
    }
  };

  // 進入編輯器
  const handleEdit = (flow) => {
    setEditingFlow(flow);
  };

  // 回到列表
  const handleBack = () => {
    setEditingFlow(null);
    fetchFlows();
  };

  // 刪除
  const handleDeleteConfirm = async () => {
    const flow = deleteDialog.flow;
    if (!flow) return;
    const result = await voiceFlowService.deleteFlow(flow.id);
    if (result.success) {
      setNotification({ open: true, message: `已刪除: ${flow.name}`, type: 'success' });
      setFlows(prev => prev.filter(f => f.id !== flow.id));
    } else {
      setNotification({ open: true, message: result.message, type: 'error' });
    }
    setDeleteDialog({ open: false, flow: null });
  };

  // 複製
  const handleDuplicateConfirm = async () => {
    const { flow, newName } = duplicateDialog;
    if (!flow || !newName.trim()) return;
    const result = await voiceFlowService.duplicateFlow(flow.id, {
      newName: newName.trim()
    });
    if (result.success) {
      setNotification({ open: true, message: `已複製: ${newName}`, type: 'success' });
      fetchFlows();
    } else {
      setNotification({ open: true, message: result.message, type: 'error' });
    }
    setDuplicateDialog({ open: false, flow: null, newName: '' });
  };

  // 如果在編輯模式 → 顯示 ScriptEditor
  if (editingFlow) {
    return (
      <Layout>
        <ScriptEditor
          flowId={editingFlow.id}
          flowMeta={editingFlow}
          onBack={handleBack}
        />
      </Layout>
    );
  }

  // 流程列表
  return (
    <Layout>
      <Box sx={{ p: 3, maxWidth: 1200 }}>
        {/* 頁面標題 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              語音對話腳本
            </Typography>
            <Typography variant="body2" color="text.secondary">
              管理和編輯 AI 語音助理的對話流程
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            disabled
            sx={{ textTransform: 'none' }}
          >
            從範本建立
          </Button>
        </Box>

        {/* 篩選 */}
        <Tabs
          value={triggerFilter}
          onChange={(e, v) => setTriggerFilter(v)}
          sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none', minHeight: 40 } }}
        >
          <Tab label="全部" value="all" />
          <Tab label="進線 (Inbound)" value="inbound" />
          <Tab label="外撥 (Outbound)" value="outbound" />
          <Tab label="中斷處理" value="interrupt" />
        </Tabs>

        {/* 流程卡片 */}
        <FlowCardGrid
          flows={flows}
          loading={loading}
          onEdit={handleEdit}
          onToggle={handleToggle}
          onDuplicate={(flow) => setDuplicateDialog({
            open: true,
            flow,
            newName: `${flow.name} (副本)`
          })}
          onDelete={(flow) => setDeleteDialog({ open: true, flow })}
        />
      </Box>

      {/* 刪除確認對話框 */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, flow: null })}
      >
        <DialogTitle>確認刪除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            確定要刪除流程「{deleteDialog.flow?.name}」嗎？此操作無法復原。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, flow: null })}>
            取消
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            刪除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 複製對話框 */}
      <Dialog
        open={duplicateDialog.open}
        onClose={() => setDuplicateDialog({ open: false, flow: null, newName: '' })}
      >
        <DialogTitle>複製流程</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            為複製的流程命名：
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="新流程名稱"
            value={duplicateDialog.newName}
            onChange={(e) => setDuplicateDialog(prev => ({ ...prev, newName: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateDialog({ open: false, flow: null, newName: '' })}>
            取消
          </Button>
          <Button
            onClick={handleDuplicateConfirm}
            variant="contained"
            disabled={!duplicateDialog.newName.trim()}
          >
            複製
          </Button>
        </DialogActions>
      </Dialog>

      {/* 通知 */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={notification.type}
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default VoiceFlowPage;
